import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Flex, Stack, HStack, VStack, Text, Heading, Badge, Spinner, Button, IconButton, Input, InputGroup, InputLeftElement,
  Tabs, TabList, Tab, TabPanels, TabPanel, useToast, Alert, AlertIcon, Stat, StatLabel, StatNumber, SimpleGrid, Divider, Select, Tag,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton, useDisclosure, FormControl, FormLabel,
  Checkbox,
} from '@chakra-ui/react';
import { SearchIcon, ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { FaMapMarkerAlt, FaClock, FaListUl, FaMapMarkedAlt, FaSignOutAlt, FaCalendarAlt, FaUser, FaClipboardCheck, FaStickyNote, FaCog, FaPhone, FaSms, FaMoneyCheckAlt, FaRoute, FaLocationArrow } from 'react-icons/fa';
// MarkerF + PolylineF (functional variants) are the React-18-StrictMode-safe
// versions in @react-google-maps/api v2. Their legacy equivalents silently
// drop on StrictMode's double mount/unmount cycle — pins didn't show and the
// route polyline was invisible despite all the surrounding logic being
// correct. There's no DirectionsRendererF in v2, so we extract the
// overview_path from the route response and draw it ourselves via PolylineF.
import {
  GoogleMap,
  useJsApiLoader,
  MarkerF as Marker,
  InfoWindow,
  PolylineF,
} from '@react-google-maps/api';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import { selectAll } from '../../utils/fetchAllRows';
import { getOutcomeConfig } from '../../utils/statusBadge';
import { getDrawAvailability, isAvailableToSchedule, isCancelled } from '../../utils/drawAvailability';
import { formatSchedule, scheduledOnDate, scheduledRange } from '../../utils/schedule';
import { logAssignmentChange } from '../../utils/logOutcome';

// Local YYYY-MM-DD helpers for the day-scoped "Assigned" tab.
const ymdLocal = (dt) => { const p = (n) => String(n).padStart(2, '0'); return `${dt.getFullYear()}-${p(dt.getMonth() + 1)}-${p(dt.getDate())}`; };
const todayYmd = () => ymdLocal(new Date());
const shiftYmd = (ymd, days) => {
  const [y, m, d] = ymd.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + days);
  return ymdLocal(dt);
};
const dayLabel = (ymd) => {
  const t = todayYmd();
  if (ymd === t) return 'Today';
  if (ymd === shiftYmd(t, -1)) return 'Yesterday';
  if (ymd === shiftYmd(t, 1)) return 'Tomorrow';
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
};
import { getPhlebReadinessStage, isRouteReady } from '../../utils/phlebReadiness';
import { NYC_REGION_CHOICES } from '../../utils/nycRegions';
import { SERVICE_LOCATIONS } from '../../utils/serviceLocations';
import { tagColor } from '../../utils/submissionTags';
import OutcomeDrawer from './OutcomeDrawer';
import PhlebTour from './PhlebTour';
import { GOOGLE_MAPS_LOADER_OPTIONS } from '../../utils/googleMapsLoader';
import { FaRegQuestionCircle } from 'react-icons/fa';

const NYC_CENTER = { lat: 40.7128, lng: -74.006 };

// The Open tab now uses a draw-level matcher (not just the status string) so we can
// hide rows that have a do-not-draw outcome even if their status is still 'pending'.
// The other tabs keep their original status-only logic.
const STATUS_TABS = [
  // Default landing for a phleb: everything still on their plate — not yet drawn,
  // not dropped off, not cancelled. So a phleb never lands on an empty screen
  // while they have assigned in-progress work.
  { value: 'active',      label: 'Active',      matchDraw: (d) => {
    if (isCancelled(d)) return false;
    const code = getDrawAvailability(d).code;
    return code !== 'drawn' && code !== 'done';
  }},
  { value: 'open',        label: 'Open',        matchDraw: (d) => isAvailableToSchedule(d) },
  { value: 'in_progress', label: 'In Progress', match: (s) => s === 'in_progress' || s === 'scheduled' },
  // "Completed" from the phleb's perspective = they've drawn the patient. The
  // submissions.status doesn't actually flip to 'completed' on a successful draw
  // (it goes to waiting_to_be_received → waiting_on_lab_results → completed).
  // So we match on the drawAvailability classification, which lumps both
  // "drawn" (sample collected, en route) and "done" (results posted) together.
  { value: 'completed',   label: 'Completed',   matchDraw: (d) => {
    const code = getDrawAvailability(d).code;
    return code === 'drawn' || code === 'done';
  }},
  { value: 'cancelled',   label: 'Cancelled',   matchDraw: (d) => isCancelled(d) },
  { value: 'all',         label: 'All',         match: () => true },
];

const ASSIGN_TABS = [
  { value: 'mine',       label: 'Mine' },
  { value: 'unassigned', label: 'Available' },
  { value: 'all',        label: 'All' },
];

function fmtAddress(d) {
  const street = d.patient_street || d.patient_address || '';
  return [street, d.patient_city, d.patient_state, d.patient_zip].filter(Boolean).join(', ');
}

function fmtDate(d) {
  if (!d) return '';
  try { return new Date(d).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return d; }
}

// Strip non-dial characters so the OS handler doesn't choke on "(555) 123-4567"
// style input. Returns null if nothing dial-able is left.
function dialDigits(phone) {
  if (!phone) return null;
  const cleaned = String(phone).replace(/[^\d+]/g, '');
  return cleaned || null;
}

export default function PhlebDrawsView() {
  const { id: phlebId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();

  const [draws, setDraws] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [statusTab, setStatusTab] = useState('active');
  const [assignTab, setAssignTab] = useState('mine');
  const [assignedDay, setAssignedDay] = useState(() => todayYmd()); // day shown in the Assigned tab
  const [view, setView] = useState('list');
  const [selectedId, setSelectedId] = useState(null);
  const [outcomeFor, setOutcomeFor] = useState(null); // submission row whose outcome drawer is open
  const [geocoded, setGeocoded] = useState({}); // submissionId → {lat,lng}
  const geocodeAttempted = useRef(new Set());
  const [phlebProfile, setPhlebProfile] = useState(null);
  const [serviceArea, setServiceArea] = useState(true); // default: only show ZIPs the phleb covers
  const [selectedRegions, setSelectedRegions] = useState([]); // array of region keys
  const [tourOpen, setTourOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  // Drop-batch proof-of-drop photo flow.
  const { isOpen: dropOpen, onOpen: openDrop, onClose: closeDrop } = useDisclosure();
  const [dropPhotoUrl, setDropPhotoUrl] = useState('');
  const [dropUploading, setDropUploading] = useState(false);
  const [dropSaving, setDropSaving] = useState(false);
  // Create-route flow
  const { isOpen: isRouteOpen, onOpen: onRouteOpen, onClose: onRouteClose } = useDisclosure();
  const [routeStart, setRouteStart] = useState('');
  const [routeBuilding, setRouteBuilding] = useState(false);
  // Which of the eligible routeStops the phleb has picked to include. A Set
  // of submission IDs. Default behavior: every eligible stop is selected on
  // first open and any newly-eligible stops are auto-added so the phleb can
  // opt OUT rather than opt IN. The sync effect below keeps it pruned as
  // draws come and go.
  const [selectedStopIds, setSelectedStopIds] = useState(() => new Set());
  const [routeActive, setRouteActive] = useState(false);          // showing the in-page route map
  const [routeDirections, setRouteDirections] = useState(null);   // DirectionsResult for the renderer
  const [routeInfo, setRouteInfo] = useState(null);               // { miles, minutes }
  const [routeMapsUrl, setRouteMapsUrl] = useState('');           // optional hand-off to Google Maps
  const [orderedStops, setOrderedStops] = useState([]);           // optimized stop order w/ positions
  const [stopIndex, setStopIndex] = useState(0);                  // which stop the banner/map focuses on
  const [myLocation, setMyLocation] = useState(null);             // live GPS { lat, lng }
  // Travel preference for per-leg directions ('driving' | 'transit'), remembered
  // per device. The optimized multi-stop overview stays driving (Google can't
  // optimize a transit route), but per-leg hand-off + estimate honor this.
  const travelPrefKey = `phleb_travel_pref_${phlebId || 'me'}`;
  const [travelPref, setTravelPref] = useState(() => {
    try { return localStorage.getItem(`phleb_travel_pref_${phlebId || 'me'}`) === 'transit' ? 'transit' : 'driving'; } catch { return 'driving'; }
  });
  const [legEstimate, setLegEstimate] = useState(null); // { text, mode } for the current leg
  const routeMapRef = useRef(null);
  const geoWatchRef = useRef(null);
  const lastLocWriteRef = useRef(0);

  // GPS sharing opt-out — the phleb can turn off location sharing entirely so we
  // never read or upload her position. Persisted per-phleb on this device.
  const gpsBlockKey = `phleb_gps_blocked_${phlebId || 'me'}`;
  const [gpsBlocked, setGpsBlocked] = useState(() => {
    try { return localStorage.getItem(`phleb_gps_blocked_${phlebId || 'me'}`) === '1'; } catch { return false; }
  });
  // Browser-level geolocation permission state ('granted'|'denied'|'prompt'),
  // so we can show a "location is blocked — here's how to enable" helper.
  const [geoPermission, setGeoPermission] = useState(null);
  useEffect(() => {
    let perm;
    const onChange = () => setGeoPermission(perm?.state || null);
    if (navigator.permissions?.query) {
      navigator.permissions.query({ name: 'geolocation' })
        .then((p) => { perm = p; setGeoPermission(p.state); p.onchange = onChange; })
        .catch(() => {});
    }
    return () => { if (perm) perm.onchange = null; };
  }, []);

  // Load identity info so we can show "you're signed in as …" across the top.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('phlebotomist_profiles')
        .select('id, full_name, email, company_name, company_address, service_zips, onboarding_token, onboarding_status, onboarding_completed_at, supplies_picked_up_at, route_ready_at')
        .eq('id', phlebId)
        .maybeSingle();
      if (!cancelled) setPhlebProfile(data || null);
    })();
    return () => { cancelled = true; };
  }, [phlebId]);

  const phlebZips = useMemo(() => {
    if (!Array.isArray(phlebProfile?.service_zips)) return new Set();
    return new Set(phlebProfile.service_zips.map((z) => String(z)));
  }, [phlebProfile]);

  // Union of all ZIPs from the regions the phleb has selected. Empty set = no
  // region filter (all ZIPs allowed).
  const regionZips = useMemo(() => {
    if (selectedRegions.length === 0) return null;
    const s = new Set();
    for (const key of selectedRegions) {
      const choice = NYC_REGION_CHOICES.find((c) => c.key === key);
      if (choice) for (const z of choice.zips) s.add(String(z));
    }
    return s;
  }, [selectedRegions]);

  const initials = (phlebProfile?.full_name || phlebProfile?.email || '?')
    .split(/[\s@.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0].toUpperCase())
    .join('');

  const handleOutcomeLogged = (updated) => {
    setDraws((rows) => rows.map((r) => (r.id === updated.id ? { ...r, ...updated } : r)));
  };

  const { isLoaded } = useJsApiLoader(GOOGLE_MAPS_LOADER_OPTIONS);

  // Pull every submission — the assignment filter below lets the phleb
  // toggle between "Mine", "Available" (unassigned), and "All". Phlebs
  // can now claim open draws by tapping a row.
  const fetchDraws = useCallback(async () => {
    try {
      const { data, error } = await selectAll(() => supabase
        .from('submissions')
        .select('id, draw_code, lab_order_number, patient_name, patient_email, patient_phone, patient_dob, patient_street, patient_city, patient_state, patient_zip, patient_lat, patient_lng, status, submitted_at, blood_collection_time, scheduling_type, availability_start_date, availability_end_date, availability_start_time, availability_end_time, special_instructions, phlebotomist_id, phlebotomist_email, route_confirmed_at, doctor_name, outcome_code, outcome_notes, outcome_logged_at, followup_required, tags, drop_location, spin_photo_url, ready_for_drop_at, drop_photo_url')
        .order('blood_collection_time', { ascending: true, nullsFirst: false })
        .order('submitted_at', { ascending: false }));
      if (error) throw error;
      setDraws(data || []);
    } catch (e) {
      toast({ title: 'Could not load draws', description: e.message, status: 'error', duration: 5000 });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  // Initial load + realtime sync.
  // - postgres_changes UPDATE on submissions: refetch when admin (or another
  //   surface) changes status / phleb assignment / anything on a row. Same
  //   pattern as LabPortalDashboard so the phleb side stays in sync.
  // - visibilitychange: belt-and-suspenders refetch when the tab comes back
  //   into focus, in case the websocket dropped while the tab was hidden.
  useEffect(() => {
    fetchDraws();
    const channel = supabase
      .channel('phleb-draws-realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'submissions' }, () => {
        fetchDraws();
      })
      .subscribe();
    const onVisible = () => {
      if (document.visibilityState === 'visible') fetchDraws();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      supabase.removeChannel(channel);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [fetchDraws]);

  // A draw counts as "his" if its phlebotomist_id UUID matches OR its
  // phlebotomist_email matches this phleb's profile email. The email fallback
  // covers draws assigned by name/email (e.g. admin edits, lab imports) that
  // never had the UUID column populated.
  const profileEmail = (phlebProfile?.email || '').trim().toLowerCase();
  const isMine = (d) =>
    (!!d.phlebotomist_id && d.phlebotomist_id === phlebId) ||
    (!!profileEmail && (d.phlebotomist_email || '').trim().toLowerCase() === profileEmail);
  const isUnassigned = (d) => !d.phlebotomist_id && !d.phlebotomist_email;

  const assignMatch = (d) => {
    if (assignTab === 'mine') return isMine(d);
    if (assignTab === 'unassigned') return isUnassigned(d);
    return true;
  };

  // Helper: each tab can match either by raw status string OR by inspecting the
  // whole draw row (matchDraw). Open uses matchDraw to honor outcome-level overrides.
  const tabMatches = (tab, d) => {
    if (typeof tab.matchDraw === 'function') return tab.matchDraw(d);
    return tab.match(d.status);
  };

  // Shared geographic filter so the counts agree with what the map / list
  // actually renders. Previously the counts ignored service-area + region,
  // so "Available (50)" with a 1-ZIP filter would imply 50 pins on the map
  // while the map (which DOES filter) showed 0. Now they agree.
  const inGeoScope = (d) => {
    // A draw assigned to this phleb is always in scope — if it's his job, he
    // should see it regardless of the service-area ZIP or region filters. The
    // geo filters only exist to narrow the *unassigned* discovery pool.
    if (isMine(d)) return true;
    const zip = String(d.patient_zip || '');
    if (serviceArea && phlebZips.size > 0 && zip && !phlebZips.has(zip)) return false;
    if (regionZips && zip && !regionZips.has(zip)) return false;
    if (regionZips && !zip) return false;
    return true;
  };

  // Assignment-tab counts respect the active status filter + geo scope so the
  // badge counts match what the phleb will actually see when they click the tab.
  const assignCounts = useMemo(() => {
    const tab = STATUS_TABS.find((t) => t.value === statusTab) || STATUS_TABS.find((t) => t.value === 'all');
    const inStatus = (d) => tabMatches(tab, d);
    const scoped = draws.filter((d) => inStatus(d) && inGeoScope(d));
    return {
      mine:       scoped.filter((d) => isMine(d)).length,
      unassigned: scoped.filter((d) => isUnassigned(d)).length,
      all:        scoped.length,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draws, phlebId, statusTab, serviceArea, phlebZips, regionZips]);

  const counts = useMemo(() => {
    const scoped = draws.filter((d) => assignMatch(d) && inGeoScope(d));
    const c = { active: 0, open: 0, in_progress: 0, completed: 0, cancelled: 0, all: scoped.length };
    const activeTab    = STATUS_TABS.find((t) => t.value === 'active');
    const openTab      = STATUS_TABS.find((t) => t.value === 'open');
    const progressTab  = STATUS_TABS.find((t) => t.value === 'in_progress');
    const completedTab = STATUS_TABS.find((t) => t.value === 'completed');
    const cancelledTab = STATUS_TABS.find((t) => t.value === 'cancelled');
    for (const d of scoped) {
      // Order matters: cancelled is checked before completed because outcome
      // takes precedence over status ('completed' status + 'patient_refused'
      // outcome should count as cancelled, not completed).
      if (tabMatches(activeTab, d)) c.active++;
      if (tabMatches(cancelledTab, d)) c.cancelled++;
      else if (tabMatches(openTab, d)) c.open++;
      else if (tabMatches(progressTab, d)) c.in_progress++;
      else if (tabMatches(completedTab, d)) c.completed++;
    }
    return c;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draws, assignTab, phlebId, serviceArea, phlebZips, regionZips]);

  const filtered = useMemo(() => {
    const tab = STATUS_TABS.find((t) => t.value === statusTab) || STATUS_TABS.find((t) => t.value === 'all');
    const q = query.trim().toLowerCase();
    return draws.filter((d) => {
      // Show the phleb's own draws + still-available (unassigned) ones; the
      // list groups them into "My Route" and "Available" sections below.
      if (!(isMine(d) || isUnassigned(d))) return false;
      if (!tabMatches(tab, d)) return false;
      if (!inGeoScope(d)) return false;
      if (!q) return true;
      const hay = [d.patient_name, d.draw_code, d.lab_order_number, fmtAddress(d), d.doctor_name].filter(Boolean).join(' ').toLowerCase();
      return hay.includes(q);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draws, statusTab, query, phlebId, serviceArea, phlebZips, regionZips]);

  // Split the in-scope draws into the two sections the phleb view shows.
  // My Route = assigned to me AND I've confirmed it. Available = unassigned, or
  // assigned to me by admin but not yet confirmed (the phleb has to accept it).
  const mineDraws = useMemo(() => filtered.filter((d) => isMine(d) && d.route_confirmed_at), [filtered]); // eslint-disable-line react-hooks/exhaustive-deps
  // Assigned tab: draws assigned to this phleb AND scheduled on the chosen day.
  // Not geo-scoped (it's their own work) and not gated by the status tab; only
  // cancelled draws are dropped. Sorted by scheduled time within the day.
  const assignedDayDraws = useMemo(
    () => draws
      .filter((d) => isMine(d) && !isCancelled(d) && (assignedDay === 'none'
        ? !scheduledRange(d)
        : scheduledOnDate(d, assignedDay)))
      .sort((a, b) => String(a.blood_collection_time || '').localeCompare(String(b.blood_collection_time || ''))),
    [draws, phlebId, assignedDay], // eslint-disable-line react-hooks/exhaustive-deps
  );

  // Every day that has at least one of my assigned draws, with counts — the
  // Assigned tab shows these as chips so no scheduled day goes unnoticed.
  // 'none' collects assigned draws with no scheduled date at all.
  const assignedDayChips = useMemo(() => {
    const counts = new Map();
    let noDate = 0;
    for (const d of draws) {
      if (!isMine(d) || isCancelled(d)) continue;
      const r = scheduledRange(d);
      if (!r) { noDate += 1; continue; }
      // Walk the window's days (clamped, so a bad range can't hang the UI).
      let day = r.start;
      for (let i = 0; i < 31 && day <= r.end; i++) {
        counts.set(day, (counts.get(day) || 0) + 1);
        day = shiftYmd(day, 1);
      }
    }
    // Only today and future days become chips — past days would pile up
    // forever (they stay reachable through the date picker).
    const t = todayYmd();
    const days = Array.from(counts.entries())
      .filter(([day]) => day >= t)
      .map(([day, count]) => ({ day, count }))
      .sort((a, b) => a.day.localeCompare(b.day));
    return { days, noDate };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draws, phlebId]);
  const availableDraws = useMemo(() => filtered.filter((d) => isUnassigned(d) || (isMine(d) && !d.route_confirmed_at)), [filtered]); // eslint-disable-line react-hooks/exhaustive-deps

  // Spun + photographed, waiting to be dropped — drivable as a batch. Includes
  // 'transit_to_dropoff' (spun and on the way) so a specimen doesn't fall off
  // the list just because the phleb tapped the next step on the detail page.
  const READY_TO_DROP_CODES = ['spun', 'transit_to_dropoff'];
  const readyDraws = useMemo(() => draws.filter((d) => isMine(d) && READY_TO_DROP_CODES.includes(d.outcome_code)), [draws]); // eslint-disable-line react-hooks/exhaustive-deps
  // The "Ready to drop" tab is always shown (even at 0) so phlebs always know
  // where dropped specimens go. Its empty state explains how to fill it.
  // The phleb's finished draws (dropped/completed) — shown on the Completed tab.
  const completedDraws = useMemo(() => {
    const q = query.trim().toLowerCase();
    return draws.filter((d) => {
      if (!isMine(d)) return false;
      const done = ['dropped_off_lab', 'shipped'].includes(d.outcome_code) || ['completed', 'waiting_on_lab_results'].includes(d.status);
      if (!done) return false;
      if (!q) return true;
      return [d.patient_name, d.draw_code, d.lab_order_number, fmtAddress(d)].filter(Boolean).join(' ').toLowerCase().includes(q);
    });
  }, [draws, query]); // eslint-disable-line react-hooks/exhaustive-deps

  const selected = useMemo(() => draws.find((d) => d.id === selectedId) || null, [draws, selectedId]);

  // Guided tour. Auto-start when the onboarding link carries ?tour=1, then strip
  // the param so a refresh doesn't reopen it.
  useEffect(() => {
    if (searchParams.get('tour') === '1') {
      setTourOpen(true);
      const p = new URLSearchParams(searchParams);
      p.delete('tour');
      setSearchParams(p, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // A real draw to open when the tour reaches the visit walkthrough.
  const sampleDrawId = (mineDraws[0] || availableDraws[0] || completedDraws[0] || draws[0])?.id;

  // List-page tour steps anchored to real elements via [data-tour="…"]. before()
  // switches tabs/view so each target is visible when the step measures. The
  // final step opens a real patient page and the tour continues there.
  const tourSteps = [
    { selector: '[data-tour="tabs"]', title: 'Your Draws', before: () => { setView('list'); setAssignTab('mine'); },
      body: 'This is your home base. Every patient you need to visit shows up here as a card.' },
    { selector: '[data-tour="tabs"]', title: 'Four lists',
      body: 'My Route = your confirmed work. Available = draws you can take (and admin-assigned ones awaiting your OK). Ready to drop = spun specimens. Completed = finished.' },
    { selector: '[data-tour="draw-card"]', title: 'Take a draw', before: () => { setView('list'); setAssignTab('unassigned'); },
      body: 'On Available, tap “Add to route” on a card to claim it — it moves to My Route. Tap “Open” to see full details.' },
    { selector: '[data-tour="draw-card"]', title: 'Navigate to the patient',
      body: 'Use the Map / Directions buttons on a card to drive there. Call / Text buttons reach the patient.' },
    { selector: '[data-tour="search"]', title: 'Search & switch views',
      body: 'Search by patient, code, or address. Toggle List / Map on the right.' },
    { selector: '[data-tour="service-area"]', title: 'Your service area',
      body: 'Filter draws to the ZIP codes you cover. Set those (and your address) in Settings.' },
    { title: 'Now let’s open a patient', before: () => setAssignTab('mine'),
      body: sampleDrawId
        ? 'Tap Done to open a real patient page — the tour continues there so you can see exactly what a visit looks like.'
        : 'When you have a draw, open it to run the visit: en route → arrive → tube guide → draw → spin + photo → drop. That’s the full flow!' },
  ];

  // "Add to route" — claim an available draw for this phleb (assigns it to them),
  // moving it into the My Route section. Optimistic local update; realtime sync
  // reconciles.
  const claimDraw = async (draw) => {
    if (!phlebId || !draw?.id) return;
    const nowIso = new Date().toISOString();
    // Confirm it to my route; also claim it if it was still unassigned.
    const patch = { route_confirmed_at: nowIso, updated_at: nowIso };
    const claiming = !draw.phlebotomist_id; // only assigns when it was unassigned
    if (claiming) patch.phlebotomist_id = phlebId;
    try {
      const { error } = await supabase.from('submissions').update(patch).eq('id', draw.id);
      if (error) throw error;
      setDraws((prev) => prev.map((d) => (d.id === draw.id ? { ...d, ...patch } : d)));
      if (claiming) {
        logAssignmentChange({
          submissionId: draw.id,
          fromName: null,
          toName: phlebProfile?.full_name || phlebProfile?.company_name || phlebProfile?.email || 'me',
          actorKind: 'phleb',
          actorId: phlebId,
          actorName: phlebProfile?.full_name || phlebProfile?.company_name || null,
        });
      }
      toast({ title: 'Added to your route', status: 'success', duration: 2000, isClosable: true });
    } catch (e) {
      toast({ title: 'Could not add to route', description: e.message, status: 'error', duration: 4000, isClosable: true });
    }
  };

  // Drop the whole batch. Opens the proof-of-drop modal; the actual update runs
  // in confirmDropBatch once a photo is taken.
  const dropBatch = () => {
    if (readyDraws.length === 0) return;
    setDropPhotoUrl('');
    openDrop();
  };

  // Upload the proof-of-drop photo to the shared uploads bucket.
  const uploadDropPhoto = async (file) => {
    if (!file) return;
    setDropUploading(true);
    try {
      const ext = (file.name?.split('.').pop() || 'jpg').toLowerCase();
      const path = `drop-proofs/${phlebId || 'phleb'}-${Date.now()}.${ext}`;
      const { error } = await supabase.storage.from('uploads').upload(path, file, { contentType: file.type || 'image/jpeg', upsert: true });
      if (error) throw error;
      const { data } = supabase.storage.from('uploads').getPublicUrl(path);
      setDropPhotoUrl(data.publicUrl);
      toast({ title: 'Photo added', status: 'success', duration: 1500 });
    } catch (e) {
      toast({ title: 'Upload failed', description: e.message, status: 'error', duration: 4000, isClosable: true });
    } finally {
      setDropUploading(false);
    }
  };

  // Mark every spun/ready draw as dropped at the lab, attaching the proof photo,
  // and move them to Completed (stays assigned to this phleb).
  const confirmDropBatch = async () => {
    const ids = readyDraws.map((d) => d.id);
    if (ids.length === 0) return;
    if (!dropPhotoUrl) { toast({ title: 'Take a proof-of-drop photo first', status: 'warning', duration: 3000 }); return; }
    setDropSaving(true);
    const nowIso = new Date().toISOString();
    try {
      const { error } = await supabase.from('submissions').update({
        outcome_code: 'dropped_off_lab',
        outcome_logged_at: nowIso,
        status: 'completed',
        dropped_off_at: nowIso,
        ready_for_drop_at: null,
        drop_photo_url: dropPhotoUrl,
      }).in('id', ids);
      if (error) throw error;
      setDraws((prev) => prev.map((d) => (ids.includes(d.id)
        ? { ...d, outcome_code: 'dropped_off_lab', status: 'completed', dropped_off_at: nowIso, ready_for_drop_at: null, drop_photo_url: dropPhotoUrl }
        : d)));
      toast({ title: `Dropped ${ids.length} — moved to Completed`, status: 'success', duration: 2500, isClosable: true });
      setDropPhotoUrl('');
      closeDrop();
    } catch (e) {
      toast({ title: 'Could not drop batch', description: e.message, status: 'error', duration: 4000, isClosable: true });
    } finally {
      setDropSaving(false);
    }
  };

  // ── Create route ──────────────────────────────────────────────────────────
  // Every draw assigned to this phleb that still needs a visit and has a usable
  // location (lat/lng preferred, else a street address). These are the stops.
  const routeStops = useMemo(() => {
    return draws
      .filter((d) => isMine(d))
      .filter((d) => {
        const code = getDrawAvailability(d).code;
        return code === 'available' || code === 'in_progress';
      })
      .map((d) => {
        const loc = (d.patient_lat != null && d.patient_lng != null)
          ? `${d.patient_lat},${d.patient_lng}`
          : fmtAddress(d);
        return loc ? { id: d.id, name: d.patient_name || 'Patient', loc, address: fmtAddress(d) } : null;
      })
      .filter(Boolean);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draws, phlebId, profileEmail]);

  // Keep selectedStopIds in sync with routeStops:
  // - Auto-include any newly-eligible stop (opt-out model: default is "all in")
  // - Drop any IDs that no longer represent an eligible stop (e.g. phleb logged
  //   a successful draw, so it moved out of the route candidates)
  useEffect(() => {
    const eligibleIds = new Set(routeStops.map((s) => s.id));
    setSelectedStopIds((prev) => {
      const next = new Set();
      // Keep previously-selected IDs that are still eligible.
      for (const id of prev) if (eligibleIds.has(id)) next.add(id);
      // Add any NEW eligible IDs the phleb hasn't seen yet.
      for (const id of eligibleIds) if (!prev.has(id)) {
        // First-time addition: only auto-include if either the previous set
        // was the "all selected" superset (i.e. prev.size === routeStops.length
        // before this change), OR if prev was empty (first ever render).
        // Simpler heuristic: always auto-include new stops. Matches the
        // expected admin/phleb mental model of "by default I'll go everywhere
        // assigned to me unless I opt out."
        next.add(id);
      }
      // Avoid triggering a re-render when nothing actually changed.
      if (next.size === prev.size) {
        let same = true;
        for (const id of next) if (!prev.has(id)) { same = false; break; }
        if (same) return prev;
      }
      return next;
    });
  }, [routeStops]);

  // Pull the phone's GPS and drop it into the start field as "lat,lng".
  // If GPS fails (permission denied / browser restriction / desktop with no
  // GPS hardware), fall back to the phleb's saved company_address from their
  // onboarding profile — better starting point than making them type an
  // address from scratch.
  const useMyLocation = () => {
    const fallbackToProfile = (reason) => {
      const saved = phlebProfile?.company_address?.trim();
      if (saved) {
        setRouteStart(saved);
        toast({
          title: 'Using saved address',
          description: reason
            ? `${reason} — used your onboarding address instead.`
            : 'Filled with your onboarding address.',
          status: 'info', duration: 3000,
        });
      } else {
        toast({
          title: 'Could not get your location',
          description: 'Type a starting address instead. (Add a company address in Settings to skip this step next time.)',
          status: 'error', duration: 4000,
        });
      }
    };
    if (gpsBlocked) {
      fallbackToProfile('GPS sharing is off');
      return;
    }
    if (!navigator.geolocation) {
      fallbackToProfile('Location not supported on this device');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => setRouteStart(`${pos.coords.latitude.toFixed(6)},${pos.coords.longitude.toFixed(6)}`),
      () => fallbackToProfile('GPS unavailable'),
      { enableHighAccuracy: true, timeout: 8000 }
    );
  };

  // Live-GPS plumbing — watch the phleb's position so we can show a moving "you
  // are here" dot on the route map.
  // Persist live location/route to phleb_live_locations so the admin
  // "Active Routes" page can see where each rep is and which route they're on.
  const upsertLive = async (fields) => {
    try {
      await supabase.from('phleb_live_locations').upsert(
        { phlebotomist_id: phlebId, updated_at: new Date().toISOString(), ...fields },
        { onConflict: 'phlebotomist_id' }
      );
    } catch (e) {
      console.warn('live location upsert failed:', e.message);
    }
  };

  const startLocationWatch = () => {
    if (gpsBlocked) return; // phleb opted out of sharing location
    if (!navigator.geolocation || geoWatchRef.current != null) return;
    geoWatchRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        const loc = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setMyLocation(loc);
        // Throttle DB writes to ~once every 12s.
        const now = Date.now();
        if (now - lastLocWriteRef.current > 12000) {
          lastLocWriteRef.current = now;
          upsertLive({ lat: loc.lat, lng: loc.lng, route_active: true });
        }
      },
      () => {},
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 10000 }
    );
  };
  const stopLocationWatch = () => {
    if (geoWatchRef.current != null && navigator.geolocation) {
      navigator.geolocation.clearWatch(geoWatchRef.current);
      geoWatchRef.current = null;
    }
  };

  // Toggle GPS sharing on/off. When turning off, stop any active watch and
  // clear our last shared position so the admin view no longer tracks her.
  const toggleGpsBlocked = () => {
    setGpsBlocked((prev) => {
      const next = !prev;
      try { localStorage.setItem(gpsBlockKey, next ? '1' : '0'); } catch { /* ignore */ }
      if (next) {
        stopLocationWatch();
        setMyLocation(null);
        upsertLive({ lat: null, lng: null, route_active: false });
        toast({ title: 'GPS sharing turned off', description: 'Admins can no longer see your location.', status: 'info', duration: 3000 });
      } else {
        toast({ title: 'GPS sharing turned on', status: 'success', duration: 2000 });
        if (routeActive) startLocationWatch();
      }
      return next;
    });
  };
  const closeRoute = () => {
    setRouteActive(false);
    setRouteDirections(null);
    setRouteInfo(null);
    setOrderedStops([]);
    setStopIndex(0);
    stopLocationWatch();
    upsertLive({ route_active: false });
  };

  // Origin position for the green Start pin, derived from the route response
  // (legs[0].start_location is whatever Google geocoded `origin` to). Computed
  // at component scope rather than inline in JSX so React's reconciliation
  // doesn't churn through this on every render.
  const routeOrigin = useMemo(() => {
    const sl = routeDirections?.routes?.[0]?.legs?.[0]?.start_location;
    if (!sl || typeof sl.lat !== 'function') return null;
    return { lat: sl.lat(), lng: sl.lng() };
  }, [routeDirections]);

  // Focus the route map on the currently-selected stop (never on the phleb's GPS),
  // so the banner + ◀ ▶ stepper pans to each stop in turn. The FIRST time the
  // route appears we fit-bounds to the whole route instead, so the phleb can
  // see every pin before they start stepping through.
  const focusStop = (idx) => {
    const stop = orderedStops[idx];
    if (stop?.position && routeMapRef.current) {
      routeMapRef.current.panTo(stop.position);
      routeMapRef.current.setZoom(15);
    }
  };
  // Fit the whole route into view when it first opens / when the route changes.
  useEffect(() => {
    if (!routeActive || !routeMapRef.current || !window.google) return;
    const bounds = new window.google.maps.LatLngBounds();
    let added = 0;
    if (routeOrigin) { bounds.extend(routeOrigin); added++; }
    orderedStops.forEach((s) => { if (s.position) { bounds.extend(s.position); added++; } });
    if (added >= 2) routeMapRef.current.fitBounds(bounds, 60);
    else if (added === 1) { routeMapRef.current.panTo(bounds.getCenter()); routeMapRef.current.setZoom(14); }
  }, [routeActive, routeDirections, orderedStops, routeOrigin]);
  // Then on stop-stepper change, zoom into the selected stop.
  useEffect(() => {
    if (routeActive) focusStop(stopIndex);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stopIndex]);
  // Stop watching GPS if the component unmounts mid-route.
  useEffect(() => () => stopLocationWatch(), []);

  // Estimate travel time for the CURRENT leg in the chosen mode (driving or
  // transit). One Directions call per leg/mode change — cheap and only while a
  // route is open. Transit defaults to "leave now".
  useEffect(() => {
    if (!routeActive || !window.google || orderedStops.length === 0) { setLegEstimate(null); return; }
    const stop = orderedStops[stopIndex];
    if (!stop?.address) { setLegEstimate(null); return; }
    const origin = stopIndex === 0 ? routeStart : (orderedStops[stopIndex - 1]?.address || '');
    if (!origin) { setLegEstimate(null); return; }
    let cancelled = false;
    setLegEstimate({ text: '…', mode: travelPref });
    const svc = new window.google.maps.DirectionsService();
    const req = {
      origin,
      destination: stop.address,
      travelMode: travelPref === 'transit'
        ? window.google.maps.TravelMode.TRANSIT
        : window.google.maps.TravelMode.DRIVING,
    };
    if (travelPref === 'transit') req.transitOptions = { departureTime: new Date() };
    svc.route(req, (res, status) => {
      if (cancelled) return;
      const leg = status === 'OK' && res?.routes?.[0]?.legs?.[0];
      setLegEstimate(leg?.duration?.text
        ? { text: leg.duration.text, mode: travelPref }
        : { text: travelPref === 'transit' ? 'no transit route' : null, mode: travelPref });
    });
    return () => { cancelled = true; };
  }, [routeActive, stopIndex, travelPref, orderedStops, routeStart]);

  const chooseTravelPref = (mode) => {
    setTravelPref(mode);
    try { localStorage.setItem(travelPrefKey, mode); } catch { /* ignore */ }
  };

  // Optimize the stop order (Directions API, already loaded) and render the route
  // on the in-page map alongside the phleb's live GPS — instead of leaving the app.
  const buildRoute = async (opts = {}) => {
    // Only route through the stops the phleb actually picked. routeStops is the
    // eligibility list; selectedStopIds is their subset choice. Preserves the
    // routeStops display order (which is just the draws list order — Google
    // re-optimizes anyway). opts.stopIds/opts.origin let an admin-published
    // route start it programmatically without waiting on setState.
    const idSet = opts.stopIds instanceof Set ? opts.stopIds : selectedStopIds;
    const stops = routeStops.filter((s) => idSet.has(s.id));
    const origin = (opts.origin || routeStart || '').trim();
    if (!origin) { toast({ title: 'Enter a starting point', status: 'warning', duration: 3000 }); return; }
    if (stops.length === 0) { toast({ title: 'Pick at least one stop', description: 'Tick the patients you want to visit on this route.', status: 'warning', duration: 3000 }); return; }
    if (!isLoaded || !window.google) { toast({ title: 'Map still loading', description: 'Try again in a moment.', status: 'warning', duration: 3000 }); return; }

    // Google caps a single directions request at ~25 waypoints + origin/dest.
    const MAX = 25;
    let working = stops;
    if (stops.length > MAX) {
      working = stops.slice(0, MAX);
      toast({ title: `Showing first ${MAX} stops`, description: `You have ${stops.length} — Google caps one route.`, status: 'info', duration: 5000 });
    }

    setRouteBuilding(true);
    try {
      const svc = new window.google.maps.DirectionsService();
      const destination = working[working.length - 1].loc;
      const middle = working.slice(0, -1);
      const result = await new Promise((resolve, reject) => {
        svc.route({
          origin,
          destination,
          waypoints: middle.map((s) => ({ location: s.loc, stopover: true })),
          optimizeWaypoints: true,
          travelMode: window.google.maps.TravelMode.DRIVING,
        }, (res, status) => (status === 'OK' ? resolve(res) : reject(new Error(status))));
      });

      setRouteDirections(result);

      // Totals across all legs.
      const legs = result.routes?.[0]?.legs || [];
      let dist = 0; let dur = 0;
      legs.forEach((l) => { dist += l.distance?.value || 0; dur += l.duration?.value || 0; });
      setRouteInfo({ miles: (dist / 1609.34).toFixed(1), minutes: Math.round(dur / 60) });

      // Optimized order of the actual stop objects for the stepper banner, with
      // their geocoded positions taken from the route legs (legs[i].end_location
      // is the i-th visited stop).
      const order = result.routes?.[0]?.waypoint_order || [];
      const orderedInput = (order.length ? order.map((i) => middle[i]) : middle).concat([working[working.length - 1]]);
      const stopList = orderedInput.map((s, i) => {
        const leg = legs[i];
        const pos = leg?.end_location ? { lat: leg.end_location.lat(), lng: leg.end_location.lng() } : null;
        return { id: s.id, name: s.name, address: leg?.end_address || s.loc, position: pos };
      });
      setOrderedStops(stopList);
      setStopIndex(0);

      // Optimized-order Google Maps URL for the optional turn-by-turn hand-off.
      const allOrdered = orderedInput.map((s) => s.loc);
      const urlDest = allOrdered[allOrdered.length - 1];
      const wp = allOrdered.slice(0, -1).map(encodeURIComponent).join('|');
      setRouteMapsUrl(`https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(origin)}&destination=${encodeURIComponent(urlDest)}${wp ? `&waypoints=${wp}` : ''}&travelmode=driving`);

      setRouteActive(true);
      onRouteClose();
      // Publish the route so admins can see what each rep is on.
      const startedAt = new Date().toISOString();
      upsertLive({
        route_active: true,
        route_started_at: startedAt,
        // Persist the OPTIMIZED order (stopList) so admins see the true fastest
        // route, not the order the phleb happened to tick the stops in. Also
        // persist the route totals so admin can sum commute time across reps.
        route: {
          start: origin,
          startedAt,
          totalMinutes: Math.round(dur / 60),
          totalMiles: Number((dist / 1609.34).toFixed(1)),
          stops: stopList.map((s) => ({ id: s.id, name: s.name, loc: s.position || s.address, address: s.address })),
        },
      });
      startLocationWatch();
    } catch (e) {
      toast({ title: 'Could not build route', description: e.message, status: 'error', duration: 4000, isClosable: true });
    } finally {
      setRouteBuilding(false);
    }
  };

  // Auto-open an admin-published route: if an admin built this phleb's route
  // (phleb_live_locations.route.builtByAdmin), open it on login so they just
  // see it and start. Runs once, after draws + maps are ready.
  const autoRouteRef = useRef(false);
  useEffect(() => {
    if (autoRouteRef.current) return;
    if (!isLoaded || !window.google || routeActive || routeStops.length === 0) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('phleb_live_locations')
        .select('route, route_active')
        .eq('phlebotomist_id', phlebId)
        .maybeSingle();
      if (cancelled || autoRouteRef.current) return;
      const r = data?.route;
      if (!data?.route_active || !r?.builtByAdmin || !Array.isArray(r.stops) || r.stops.length === 0) return;
      // Only stops that are still eligible on this phleb's draws.
      const eligible = new Set(routeStops.map((s) => s.id));
      const ids = new Set(r.stops.map((s) => s.id).filter((id) => eligible.has(id)));
      if (ids.size === 0) return;
      autoRouteRef.current = true;
      setSelectedStopIds(ids);
      if (r.start) setRouteStart(typeof r.start === 'string' ? r.start : '');
      buildRoute({ stopIds: ids, origin: typeof r.start === 'string' ? r.start : routeStart });
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, routeStops, phlebId]);

  const logout = async () => {
    await supabase.auth.signOut().catch(() => {});
    localStorage.removeItem('phlebotomistUser');
    navigate('/phlebotomist/login');
  };

  return (
    <Box maxW="1200px" mx="auto" p={{ base: 3, md: 6 }}>
      {/* Identity banner — makes it unambiguous which phleb is signed in. */}
      <Flex
        bgGradient="linear(to-r, blue.600, blue.500)"
        color="white"
        borderRadius="xl"
        p={{ base: 3, md: 4 }}
        mb={4}
        align="center"
        justify="space-between"
        flexWrap="wrap"
        gap={3}
      >
        <HStack spacing={3}>
          <Flex
            align="center"
            justify="center"
            bg="white"
            color="blue.700"
            boxSize={{ base: '40px', md: '48px' }}
            borderRadius="full"
            fontWeight="700"
            fontSize="md"
          >
            {initials || '?'}
          </Flex>
          <Box>
            <Text fontSize="2xs" opacity={0.85} fontWeight="600" letterSpacing="0.08em" textTransform="uppercase">
              Signed in as
            </Text>
            <Text fontSize="md" fontWeight="700" lineHeight="1.2">
              {phlebProfile?.full_name || 'Phlebotomist'}
            </Text>
            <Text fontSize="xs" opacity={0.9}>
              {phlebProfile?.email}{phlebProfile?.company_name ? ` · ${phlebProfile.company_name}` : ''}
            </Text>
          </Box>
        </HStack>
        <HStack spacing={2} flexWrap="wrap" justify="flex-end">
          <Button size="sm" colorScheme="whiteAlpha" variant="solid" leftIcon={<FaMoneyCheckAlt />} onClick={() => navigate(`/phlebotomist/${phlebId}/payslips`)}>
            Payslips
          </Button>
          <Button size="sm" colorScheme="whiteAlpha" variant="solid" leftIcon={<FaCog />} onClick={() => navigate(`/phlebotomist/${phlebId}/settings`)}>
            Settings
          </Button>
          <Button
            size="sm"
            colorScheme={gpsBlocked ? 'red' : 'whiteAlpha'}
            variant="solid"
            leftIcon={<FaLocationArrow />}
            onClick={toggleGpsBlocked}
            title={gpsBlocked ? 'Your location is private. Tap to share GPS while on routes.' : 'Tap to stop sharing your location with admins.'}
          >
            {gpsBlocked ? 'GPS off' : 'GPS on'}
          </Button>
          <Button size="sm" variant="ghost" color="white" leftIcon={<FaSignOutAlt />} onClick={logout} _hover={{ bg: 'whiteAlpha.300' }}>
            Sign out
          </Button>
        </HStack>
      </Flex>

      {/* GPS sharing status / help */}
      {gpsBlocked ? (
        <Alert status="warning" borderRadius="lg" mb={4}>
          <AlertIcon />
          <Box flex={1}>
            <Text fontSize="sm" fontWeight="600">GPS sharing is off</Text>
            <Text fontSize="xs" color="gray.600">
              Admins can't see your live location and the route map won't follow you. Routes still work from a typed/saved start address.
            </Text>
          </Box>
          <Button size="sm" colorScheme="green" onClick={toggleGpsBlocked}>Turn on</Button>
        </Alert>
      ) : geoPermission === 'denied' ? (
        <Alert status="error" borderRadius="lg" mb={4}>
          <AlertIcon />
          <Box flex={1}>
            <Text fontSize="sm" fontWeight="600">Location is blocked in your browser</Text>
            <Text fontSize="xs" color="gray.600">
              To use live routing, enable location for this site: tap the lock/ⓘ icon in the address bar → Location → Allow, then reload. On iPhone also check Settings → your browser → Location.
            </Text>
          </Box>
        </Alert>
      ) : null}

      {/* Page header */}
      <Flex mb={4} gap={3} align={{ base: 'stretch', sm: 'center' }} justify="space-between" direction={{ base: 'column', sm: 'row' }}>
        <Box>
          <Heading size="lg">Your Draws</Heading>
          <Text fontSize="sm" color="gray.500">Patients to visit in your service area.</Text>
        </Box>
        <Button
          colorScheme="purple"
          leftIcon={<Box as={FaRoute} />}
          onClick={onRouteOpen}
          isDisabled={routeStops.length === 0}
          flexShrink={0}
        >
          Create route{routeStops.length ? ` (${routeStops.length})` : ''}
        </Button>
      </Flex>

      {/* Readiness banner — shown until admin has marked the phleb route-ready.
          Walks them through the two remaining steps so they know what they're
          waiting on without having to ask. */}
      {phlebProfile && !isRouteReady(phlebProfile) && (() => {
        const readiness = getPhlebReadinessStage(phlebProfile);
        const onboarded = phlebProfile.onboarding_status === 'completed' || !!phlebProfile.onboarding_completed_at;
        const suppliesUp = !!phlebProfile.supplies_picked_up_at;
        return (
          <Alert
            status="info"
            variant="left-accent"
            borderRadius="md"
            mb={4}
            colorScheme={readiness.color === 'green' ? 'green' : 'orange'}
          >
            <AlertIcon />
            <Box flex={1}>
              <Text fontWeight="700" fontSize="sm" mb={1}>
                {!onboarded
                  ? 'Finish your onboarding to start receiving draws.'
                  : !suppliesUp
                    ? "You're set up — waiting on supplies pickup."
                    : "Supplies received — waiting on route clearance."}
              </Text>
              <HStack spacing={3} fontSize="xs" color="gray.700" flexWrap="wrap" rowGap={1}>
                <Text>
                  <Text as="span" fontWeight="700" color={onboarded ? 'green.600' : 'gray.400'}>
                    {onboarded ? '✓' : '○'}
                  </Text> Onboarding complete
                </Text>
                <Text>
                  <Text as="span" fontWeight="700" color={suppliesUp ? 'green.600' : 'gray.400'}>
                    {suppliesUp ? '✓' : '○'}
                  </Text> Supplies picked up
                </Text>
                <Text>
                  <Text as="span" fontWeight="700" color={readiness.code === 'route_ready' ? 'green.600' : 'gray.400'}>
                    {readiness.code === 'route_ready' ? '✓' : '○'}
                  </Text> Route-ready
                </Text>
              </HStack>
              <Text fontSize="2xs" color="gray.500" mt={1.5}>
                You can browse draws below, but you'll be officially assigned once admin clears you.
              </Text>
            </Box>
          </Alert>
        );
      })()}


      {/* My Route / Available tabs + service-area toggle */}
      <Flex gap={3} mb={3} flexWrap="wrap" align="center">
        <HStack data-tour="tabs" spacing={0} borderWidth="1px" borderRadius="md" overflow="hidden" bg="white" w="fit-content">
          <Button size="sm" borderRadius={0}
            variant={assignTab === 'mine' ? 'solid' : 'ghost'} colorScheme={assignTab === 'mine' ? 'blue' : 'gray'}
            onClick={() => setAssignTab('mine')}>
            My Route ({mineDraws.length})
          </Button>
          <Button size="sm" borderRadius={0}
            variant={assignTab === 'assignedDay' ? 'solid' : 'ghost'} colorScheme={assignTab === 'assignedDay' ? 'purple' : 'gray'}
            onClick={() => setAssignTab('assignedDay')}>
            Assigned ({assignedDayDraws.length})
          </Button>
          <Button size="sm" borderRadius={0}
            variant={assignTab === 'unassigned' ? 'solid' : 'ghost'} colorScheme={assignTab === 'unassigned' ? 'blue' : 'gray'}
            onClick={() => setAssignTab('unassigned')}>
            Available ({availableDraws.length})
          </Button>
          <Button size="sm" borderRadius={0}
            variant={assignTab === 'ready' ? 'solid' : 'ghost'} colorScheme={assignTab === 'ready' ? 'cyan' : 'gray'}
            onClick={() => setAssignTab('ready')}>
            Ready to drop ({readyDraws.length})
          </Button>
          <Button size="sm" borderRadius={0}
            variant={assignTab === 'completed' ? 'solid' : 'ghost'} colorScheme={assignTab === 'completed' ? 'blue' : 'gray'}
            onClick={() => setAssignTab('completed')}>
            Completed ({completedDraws.length})
          </Button>
        </HStack>

        {/* Cancelled shortcut — toggles the status filter without leaving this row. */}
        <Button
          size="sm"
          variant={statusTab === 'cancelled' ? 'solid' : 'outline'}
          colorScheme="red"
          onClick={() => setStatusTab(statusTab === 'cancelled' ? 'all' : 'cancelled')}
        >
          Cancelled ({counts.cancelled ?? 0})
        </Button>

        {phlebZips.size > 0 ? (
          <Button
            data-tour="service-area"
            size="sm"
            variant={serviceArea ? 'solid' : 'outline'}
            colorScheme={serviceArea ? 'green' : 'gray'}
            leftIcon={<Box as={FaMapMarkerAlt} />}
            onClick={() => setServiceArea(!serviceArea)}
          >
            {serviceArea ? `My service area (${phlebZips.size} ZIPs)` : 'All NYC'}
          </Button>
        ) : (
          <Button
            data-tour="service-area"
            size="sm"
            variant="outline"
            colorScheme="orange"
            leftIcon={<Box as={FaMapMarkerAlt} />}
            onClick={() => navigate(`/phlebotomist/${phlebId}/settings`)}
            title="Pick the ZIPs you can cover. Opens your settings page."
          >
            Set your service area
          </Button>
        )}

        <Button
          size="sm"
          variant="ghost"
          colorScheme="blue"
          leftIcon={<Box as={FaRegQuestionCircle} />}
          onClick={() => setTourOpen(true)}
          ml="auto"
        >
          Tour
        </Button>

        <Select
          size="sm"
          maxW="280px"
          placeholder={`+ Filter by region${selectedRegions.length > 0 ? ` (${selectedRegions.length} active)` : ''}`}
          value=""
          onChange={(e) => {
            const v = e.target.value;
            if (!v) return;
            setSelectedRegions((prev) => (prev.includes(v) ? prev : [...prev, v]));
          }}
          bg="white"
        >
          {NYC_REGION_CHOICES
            .filter((c) => !selectedRegions.includes(c.key))
            .map((c) => (
              <option key={c.key} value={c.key}>{c.key}</option>
            ))}
        </Select>
      </Flex>

      {/* Day chips for the Assigned tab — one per day that has assigned draws,
          so a draw scheduled days out is never invisible. */}
      {assignTab === 'assignedDay' && (
        <Flex gap={2} mb={3} flexWrap="wrap" align="center">
          {!assignedDayChips.days.some(({ day }) => day === todayYmd()) && (
            <Button size="sm" colorScheme="purple" variant={assignedDay === todayYmd() ? 'solid' : 'outline'}
              onClick={() => setAssignedDay(todayYmd())}>
              Today (0)
            </Button>
          )}
          {assignedDayChips.days.map(({ day, count }) => (
            <Button key={day} size="sm" colorScheme="purple"
              variant={assignedDay === day ? 'solid' : 'outline'}
              onClick={() => setAssignedDay(day)}>
              {dayLabel(day)} ({count})
            </Button>
          ))}
          {assignedDayChips.noDate > 0 && (
            <Button size="sm" colorScheme="orange"
              variant={assignedDay === 'none' ? 'solid' : 'outline'}
              onClick={() => setAssignedDay('none')}>
              No date ({assignedDayChips.noDate})
            </Button>
          )}
          <Input size="sm" type="date" w="160px" bg="white" value={assignedDay === 'none' ? '' : assignedDay}
            onChange={(e) => { if (e.target.value) setAssignedDay(e.target.value); }} />
          <Text fontSize="sm" color="gray.500">
            {assignedDayDraws.length} patient{assignedDayDraws.length === 1 ? '' : 's'}{assignedDay === 'none' ? ' without a date' : ' scheduled'}
          </Text>
        </Flex>
      )}

      {/* Active region chips */}
      {selectedRegions.length > 0 && (
        <Flex gap={2} mb={3} flexWrap="wrap" align="center">
          <Text fontSize="xs" color="gray.500" fontWeight="600">Regions:</Text>
          {selectedRegions.map((key) => (
            <Tag key={key} size="sm" colorScheme="blue" borderRadius="full">
              <Box pl={1}>{key.replace(/^[^—]+— /, '')}</Box>
              <Box
                as="button"
                ml={2}
                px={1.5}
                fontWeight="700"
                onClick={() => setSelectedRegions((prev) => prev.filter((k) => k !== key))}
                aria-label="Remove region"
              >×</Box>
            </Tag>
          ))}
          <Button size="xs" variant="ghost" onClick={() => setSelectedRegions([])}>Clear all</Button>
        </Flex>
      )}

      {/* Controls */}
      <Flex gap={3} mb={3} flexWrap="wrap">
        <InputGroup data-tour="search" size="sm" maxW={{ base: '100%', md: '320px' }}>
          <InputLeftElement pointerEvents="none"><SearchIcon color="gray.400" /></InputLeftElement>
          <Input placeholder="Search patient, code, address…" value={query} onChange={(e) => setQuery(e.target.value)} bg="white" />
        </InputGroup>
        <Select size="sm" maxW="180px" value={statusTab} onChange={(e) => setStatusTab(e.target.value)} bg="white">
          {STATUS_TABS.map((t) => (
            <option key={t.value} value={t.value}>{t.label} ({counts[t.value] ?? 0})</option>
          ))}
        </Select>
        <HStack spacing={0} ml="auto" borderWidth="1px" borderRadius="md" overflow="hidden">
          <Button size="sm" variant={view === 'list' ? 'solid' : 'ghost'} colorScheme={view === 'list' ? 'blue' : 'gray'} borderRadius={0} leftIcon={<FaListUl />} onClick={() => setView('list')}>List</Button>
          <Button size="sm" variant={view === 'map' ? 'solid' : 'ghost'} colorScheme={view === 'map' ? 'blue' : 'gray'} borderRadius={0} leftIcon={<FaMapMarkedAlt />} onClick={() => setView('map')}>Map</Button>
        </HStack>
      </Flex>

      {routeActive ? (
        <Box>
          <Flex justify="space-between" align="center" mb={2} gap={2} flexWrap="wrap">
            <HStack spacing={2}>
              <Box as={FaRoute} color="purple.500" />
              <Text fontWeight="700">Your route</Text>
              {routeInfo && (
                <Badge colorScheme="purple" borderRadius="full">{routeInfo.miles} mi · ~{routeInfo.minutes} min</Badge>
              )}
            </HStack>
            <HStack spacing={2} flexWrap="wrap">
              {/* Driving ↔ transit preference (remembered). Affects per-leg
                  directions + the time estimate; the overview map stays driving. */}
              <HStack spacing={0} borderWidth="1px" borderColor="gray.300" borderRadius="md" overflow="hidden">
                <Button size="sm" borderRadius={0}
                  colorScheme={travelPref === 'driving' ? 'blue' : 'gray'}
                  variant={travelPref === 'driving' ? 'solid' : 'ghost'}
                  onClick={() => chooseTravelPref('driving')}>🚗 Drive</Button>
                <Button size="sm" borderRadius={0}
                  colorScheme={travelPref === 'transit' ? 'blue' : 'gray'}
                  variant={travelPref === 'transit' ? 'solid' : 'ghost'}
                  onClick={() => chooseTravelPref('transit')}>🚆 Transit</Button>
              </HStack>
              <Button size="sm" variant="outline" leftIcon={<Box as={FaLocationArrow} />} onClick={() => focusStop(stopIndex)} isDisabled={!orderedStops[stopIndex]?.position}>
                Recenter
              </Button>
              {routeMapsUrl && (
                <Button as="a" href={routeMapsUrl} target="_blank" rel="noopener noreferrer" size="sm" colorScheme="blue" leftIcon={<Box as={FaMapMarkedAlt} />}>
                  Google Maps
                </Button>
              )}
              <Button size="sm" variant="ghost" onClick={closeRoute}>Close</Button>
            </HStack>
          </Flex>

          {/* Stop stepper banner — which stop you're headed to, with ◀ ▶ + details */}
          {orderedStops.length > 0 && (() => {
            const stop = orderedStops[stopIndex] || {};
            return (
              <Flex align="center" gap={2} bg="purple.600" color="white" px={2} py={2} borderTopRadius="md">
                <IconButton aria-label="Previous stop" icon={<ChevronLeftIcon boxSize={6} />} size="sm" variant="ghost" color="white"
                  _hover={{ bg: 'whiteAlpha.300' }} isDisabled={stopIndex === 0}
                  onClick={() => setStopIndex((i) => Math.max(0, i - 1))} />
                <Box flex={1} minW={0} textAlign="center">
                  <Text fontSize="2xs" textTransform="uppercase" letterSpacing="wider" opacity={0.85}>
                    Stop {stopIndex + 1} of {orderedStops.length} · headed to
                  </Text>
                  <Text fontSize="sm" fontWeight="700" noOfLines={1}>{stop.name}</Text>
                  <Text fontSize="2xs" opacity={0.9} noOfLines={1}>{stop.address}</Text>
                  {legEstimate && (
                    <Text fontSize="2xs" fontWeight="700" opacity={0.95} mt={0.5}>
                      {legEstimate.mode === 'transit' ? '🚆' : '🚗'} {legEstimate.text === '…' ? 'estimating…' : (legEstimate.text || 'n/a')}
                      {legEstimate.text && legEstimate.text !== '…' && legEstimate.text !== 'no transit route' ? ' to this stop' : ''}
                    </Text>
                  )}
                </Box>
                {stop.address && (() => {
                  // Directions for THIS leg in the chosen mode: previous stop (or
                  // route start) → this stop. Google Maps handles full train/
                  // subway/bus routing; we can't do multi-stop transit in-app.
                  const legOrigin = stopIndex === 0 ? routeStart : (orderedStops[stopIndex - 1]?.address || '');
                  const url = `https://www.google.com/maps/dir/?api=1${legOrigin ? `&origin=${encodeURIComponent(legOrigin)}` : ''}&destination=${encodeURIComponent(stop.address)}&travelmode=${travelPref}`;
                  return (
                    <Button as="a" href={url} target="_blank" rel="noopener noreferrer"
                      size="xs" colorScheme="whiteAlpha" variant="solid" title="Open directions to this stop">
                      {travelPref === 'transit' ? '🚆 Go' : '🚗 Go'}
                    </Button>
                  );
                })()}
                <Button size="xs" colorScheme="whiteAlpha" variant="solid" onClick={() => stop.id && navigate(`/phlebotomist/${phlebId}/summary/${stop.id}`)}>
                  Details
                </Button>
                <IconButton aria-label="Next stop" icon={<ChevronRightIcon boxSize={6} />} size="sm" variant="ghost" color="white"
                  _hover={{ bg: 'whiteAlpha.300' }} isDisabled={stopIndex >= orderedStops.length - 1}
                  onClick={() => setStopIndex((i) => Math.min(orderedStops.length - 1, i + 1))} />
              </Flex>
            );
          })()}

          <Box borderWidth="1px" borderRadius="md" borderTopRadius={orderedStops.length > 0 ? 0 : 'md'} overflow="hidden" h="560px" position="relative">
            {isLoaded ? (
              <GoogleMap
                mapContainerStyle={{ width: '100%', height: '100%' }}
                center={NYC_CENTER}
                zoom={11}
                options={{ mapTypeControl: false, streetViewControl: false, fullscreenControl: false }}
                onLoad={(map) => {
                  routeMapRef.current = map;
                  // Fit the route bounds IMMEDIATELY here — the fitBounds
                  // useEffect runs before onLoad on first mount and bails
                  // because routeMapRef.current is still null at that point.
                  // Without this, the map sits at NYC_CENTER zoom 11 and the
                  // markers (which live in the route extent) are invisible.
                  if (window.google) {
                    const bounds = new window.google.maps.LatLngBounds();
                    let added = 0;
                    if (routeOrigin) { bounds.extend(routeOrigin); added++; }
                    orderedStops.forEach((s) => { if (s.position) { bounds.extend(s.position); added++; } });
                    if (added >= 2) map.fitBounds(bounds, 60);
                    else if (added === 1) { map.panTo(bounds.getCenter()); map.setZoom(14); }
                  }
                }}
              >
                {/* Route polyline drawn from the route's overview_path. We draw
                    our own start + stop markers below for clarity. Using
                    PolylineF (not DirectionsRenderer) so it survives
                    StrictMode's double-mount cycle. */}
                {routeDirections?.routes?.[0]?.overview_path && (
                  <PolylineF
                    path={routeDirections.routes[0].overview_path}
                    options={{
                      strokeColor: '#805AD5',
                      strokeWeight: 5,
                      strokeOpacity: 0.85,
                      clickable: false,
                      zIndex: 50,
                    }}
                  />
                )}

                {/* Green "S" marker at the starting point. */}
                {routeOrigin && window.google && (
                  <Marker
                    position={routeOrigin}
                    title="Start"
                    label={{ text: 'S', color: '#ffffff', fontWeight: 'bold', fontSize: '13px' }}
                    zIndex={500}
                    icon={{
                      path: window.google.maps.SymbolPath.CIRCLE,
                      scale: 14,
                      fillColor: '#38A169',
                      fillOpacity: 1,
                      strokeColor: '#ffffff',
                      strokeWeight: 3,
                    }}
                  />
                )}

                {/* Numbered purple pin per stop. The currently-focused stop
                    (stopIndex) gets a brighter highlight so the phleb can tell
                    where they are in the sequence. */}
                {window.google && orderedStops.map((s, i) => {
                  if (!s.position) return null;
                  const isCurrent = i === stopIndex;
                  return (
                    <Marker
                      key={s.id || `stop-${i}`}
                      position={s.position}
                      title={`${i + 1}. ${s.name || ''}`}
                      label={{ text: String(i + 1), color: '#ffffff', fontWeight: 'bold', fontSize: '13px' }}
                      onClick={() => setStopIndex(i)}
                      zIndex={isCurrent ? 1000 : 100}
                      icon={{
                        path: window.google.maps.SymbolPath.CIRCLE,
                        scale: isCurrent ? 16 : 13,
                        fillColor: isCurrent ? '#D69E2E' : '#805AD5',
                        fillOpacity: 1,
                        strokeColor: '#ffffff',
                        strokeWeight: 3,
                      }}
                    />
                  );
                })}

                {myLocation && window.google && (
                  <Marker
                    position={myLocation}
                    title="You are here"
                    zIndex={9999}
                    icon={{
                      path: window.google.maps.SymbolPath.CIRCLE,
                      scale: 7,
                      fillColor: '#4285F4',
                      fillOpacity: 1,
                      strokeColor: '#ffffff',
                      strokeWeight: 2,
                    }}
                  />
                )}
              </GoogleMap>
            ) : (
              <Flex h="100%" align="center" justify="center"><Spinner size="lg" /></Flex>
            )}
          </Box>
          <Text fontSize="xs" color="gray.500" mt={2}>
            {myLocation ? 'Live location on — ' : 'Waiting for your GPS (allow location access) — '}
            the blue dot is you; the line is your optimized route.
          </Text>
        </Box>
      ) : loading ? (
        <Flex justify="center" py={20}><Spinner size="lg" /></Flex>
      ) : (assignTab === 'assignedDay' ? assignedDayDraws : assignTab === 'completed' ? completedDraws : assignTab === 'ready' ? readyDraws : filtered).length === 0 && view === 'list' ? (
        <Alert status="info" borderRadius="md">
          <AlertIcon />
          {assignTab === 'assignedDay'
            ? `Nothing assigned to you scheduled for ${dayLabel(assignedDay)}.`
            : "No draws to show. As patients book in your service ZIP codes, they'll appear here."}
        </Alert>
      ) : view === 'list' ? (
        (() => {
          const list = assignTab === 'assignedDay' ? assignedDayDraws : assignTab === 'unassigned' ? availableDraws : assignTab === 'completed' ? completedDraws : assignTab === 'ready' ? readyDraws : mineDraws;
          if (list.length === 0) {
            return (
              <Alert status="info" borderRadius="md">
                <AlertIcon />
                {assignTab === 'assignedDay'
                  ? `Nothing assigned to you scheduled for ${dayLabel(assignedDay)}.`
                  : assignTab === 'unassigned'
                  ? 'No available draws in your area right now.'
                  : assignTab === 'completed'
                  ? 'No completed draws yet.'
                  : assignTab === 'ready'
                  ? 'Nothing ready to drop. Spin a specimen to add it here.'
                  : 'No draws assigned to you yet.'}
              </Alert>
            );
          }
          return (
            <VStack align="stretch" spacing={2}>
              {assignTab === 'ready' && (() => {
                const locs = [...new Set(readyDraws.map((d) => d.drop_location).filter(Boolean))];
                const anyUnassigned = readyDraws.some((d) => !d.drop_location);
                let dropText, dropColor;
                if (locs.length === 1 && !anyUnassigned) { dropText = locs[0]; dropColor = 'green'; }
                else if (locs.length === 0) { dropText = 'No drop spot assigned yet — your admin sets where this goes.'; dropColor = 'orange'; }
                else { dropText = 'Multiple drop spots — check each specimen’s “Drop at”.'; dropColor = 'orange'; }
                return (
                  <Box bg="cyan.50" borderWidth="1px" borderColor="cyan.200" borderRadius="md" px={3} py={2}>
                    <Text fontSize="xs" color="cyan.700" mb={2}>Review each specimen, then drop them all together.</Text>
                    <Flex align="center" justify="flex-end" gap={2} flexWrap="wrap">
                      {/* Destination sits right beside the Drop batch button. */}
                      <HStack spacing={1.5} minW={0} bg="white" borderWidth="1px" borderColor={`${dropColor}.200`} borderRadius="md" px={2} py={1}>
                        <Icon as={FaLocationArrow} color={`${dropColor}.500`} boxSize={3} flexShrink={0} />
                        <Box minW={0}>
                          <Text fontSize="3xs" fontWeight="800" textTransform="uppercase" letterSpacing="wider" color={`${dropColor}.600`} lineHeight="1">Drop at</Text>
                          <Text fontSize="xs" fontWeight="700" color={`${dropColor}.800`} noOfLines={1}>{dropText}</Text>
                        </Box>
                      </HStack>
                      <Button size="sm" colorScheme="cyan" leftIcon={<Box as={FaClipboardCheck} />} onClick={dropBatch} flexShrink={0}>
                        Drop batch ({readyDraws.length})
                      </Button>
                    </Flex>
                  </Box>
                );
              })()}
              {list.map((d, i) => (
                <DrawRow
                  key={d.id}
                  draw={d}
                  dataTour={i === 0 ? 'draw-card' : undefined}
                  isMine={assignTab !== 'unassigned'}
                  isUnassigned={assignTab === 'unassigned'}
                  onOpen={() => navigate(`/phlebotomist/${phlebId}/summary/${d.id}`)}
                  onLogOutcome={() => setOutcomeFor(d)}
                  onAddToRoute={assignTab === 'unassigned' ? () => claimDraw(d) : undefined}
                />
              ))}
            </VStack>
          );
        })()
      ) : (
        <DrawsMap
          isLoaded={isLoaded}
          draws={assignTab === 'assignedDay' ? assignedDayDraws : assignTab === 'unassigned' ? availableDraws : assignTab === 'completed' ? completedDraws : assignTab === 'ready' ? readyDraws : mineDraws}
          geocoded={geocoded}
          setGeocoded={setGeocoded}
          geocodeAttempted={geocodeAttempted}
          selectedId={selectedId}
          setSelectedId={setSelectedId}
          onOpenDetail={(id) => navigate(`/phlebotomist/${phlebId}/summary/${id}`)}
          phlebId={phlebId}
        />
      )}

      <OutcomeDrawer
        isOpen={!!outcomeFor}
        onClose={() => setOutcomeFor(null)}
        submission={outcomeFor}
        phlebotomistId={phlebId}
        onLogged={handleOutcomeLogged}
      />

      <PhlebTour
        steps={tourSteps}
        isOpen={tourOpen}
        onClose={() => setTourOpen(false)}
        onComplete={async () => {
          // If there's a real draw, hand off to the detail page — the tour picks
          // up there (?tour=1) and logs the onboarding ack when it finishes.
          if (sampleDrawId) {
            navigate(`/phlebotomist/${phlebId}/summary/${sampleDrawId}?tour=1`);
            return;
          }
          // No draws to open — finish here and record the ack.
          try {
            await supabase.from('phleb_training_ack').insert({
              full_name: phlebProfile?.full_name || phlebProfile?.company_name || phlebProfile?.email || 'Phleb',
              email: phlebProfile?.email || null,
              phlebotomist_id: phlebId || null,
              user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
            });
          } catch (e) { /* logging shouldn't block the tour */ }
          toast({ title: 'Tour complete — nice work!', status: 'success', duration: 2500, isClosable: true });
        }}
      />

      {/* Proof-of-drop photo before finalizing the batch drop */}
      <Modal isOpen={dropOpen} onClose={closeDrop} isCentered>
        <ModalOverlay />
        <ModalContent mx={4}>
          <ModalHeader>Drop {readyDraws.length} specimen{readyDraws.length === 1 ? '' : 's'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={3}>
              <Text fontSize="sm" color="gray.700" fontWeight="600">
                Include the <Text as="span" color="cyan.700">side of the box</Text> in your photo so we can verify you dropped it in the right box.
              </Text>
              <Text fontSize="xs" color="gray.500">
                Frame it so the box’s label/side is clearly visible along with the specimens. Saved with the batch as proof.
              </Text>
              {(() => {
                const locs = [...new Set(readyDraws.map((d) => d.drop_location).filter(Boolean))];
                if (locs.length !== 1) return null;
                return (
                  <HStack spacing={1.5} bg="green.50" borderWidth="1px" borderColor="green.200" borderRadius="md" px={2} py={1.5}>
                    <Icon as={FaLocationArrow} color="green.500" boxSize={3} flexShrink={0} />
                    <Text fontSize="xs" color="green.800" fontWeight="600" noOfLines={2}>{locs[0]}</Text>
                  </HStack>
                );
              })()}
              {dropPhotoUrl ? (
                <VStack spacing={2}>
                  <Box as="img" src={dropPhotoUrl} alt="Proof of drop" maxH="240px" borderRadius="md" objectFit="contain" />
                  <Button size="sm" variant="outline" onClick={() => setDropPhotoUrl('')}>Retake photo</Button>
                </VStack>
              ) : (
                <Button as="label" htmlFor="drop-photo-input" w="full" colorScheme="cyan" leftIcon={<Box as={FaLocationArrow} />} isLoading={dropUploading} cursor="pointer">
                  Take proof-of-drop photo
                  <input id="drop-photo-input" type="file" accept="image/*" capture="environment" style={{ display: 'none' }} onChange={(e) => uploadDropPhoto(e.target.files?.[0])} />
                </Button>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={2} onClick={closeDrop}>Cancel</Button>
            <Button colorScheme="cyan" leftIcon={<Box as={FaClipboardCheck} />} onClick={confirmDropBatch} isDisabled={!dropPhotoUrl} isLoading={dropSaving}>
              Confirm drop
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* ── Create route modal ─────────────────────────────────────────────── */}
      <Modal isOpen={isRouteOpen} onClose={onRouteClose} size="md" isCentered>
        <ModalOverlay bg="blackAlpha.500" />
        <ModalContent borderRadius="xl" mx={4}>
          <ModalHeader>
            <HStack spacing={2}>
              <Box as={FaRoute} color="purple.500" />
              <Text fontSize="md">Create route</Text>
            </HStack>
            <Text fontSize="xs" color="gray.500" fontWeight="normal" mt={1}>
              {routeStops.length} assigned stop{routeStops.length === 1 ? '' : 's'} to visit
            </Text>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={4}>
              <FormControl isRequired>
                <FormLabel fontSize="sm">Starting point</FormLabel>
                <Input
                  placeholder="Enter a starting address"
                  value={routeStart}
                  onChange={(e) => setRouteStart(e.target.value)}
                />
                <Button
                  mt={2}
                  size="sm"
                  variant="outline"
                  leftIcon={<Box as={FaLocationArrow} />}
                  onClick={useMyLocation}
                >
                  Use my current location
                </Button>
              </FormControl>

              {routeStops.length > 0 && (
                <Box bg="gray.50" borderRadius="md" p={3} maxH="220px" overflowY="auto">
                  <Flex justify="space-between" align="center" mb={2}>
                    <Text fontSize="2xs" fontWeight="700" color="gray.500" textTransform="uppercase" letterSpacing="wider">
                      Stops &nbsp;
                      <Text as="span" color="purple.600" fontWeight="700">
                        {selectedStopIds.size}/{routeStops.length}
                      </Text>
                    </Text>
                    <HStack spacing={2}>
                      <Button
                        size="xs"
                        variant="link"
                        colorScheme="purple"
                        isDisabled={selectedStopIds.size === routeStops.length}
                        onClick={() => setSelectedStopIds(new Set(routeStops.map((s) => s.id)))}
                      >
                        Select all
                      </Button>
                      <Text fontSize="2xs" color="gray.300">·</Text>
                      <Button
                        size="xs"
                        variant="link"
                        colorScheme="gray"
                        isDisabled={selectedStopIds.size === 0}
                        onClick={() => setSelectedStopIds(new Set())}
                      >
                        Clear
                      </Button>
                    </HStack>
                  </Flex>
                  <VStack align="stretch" spacing={1}>
                    {routeStops.map((s) => {
                      const checked = selectedStopIds.has(s.id);
                      return (
                        <HStack
                          key={s.id}
                          as="label"
                          spacing={2}
                          fontSize="sm"
                          cursor="pointer"
                          py={1}
                          px={1}
                          borderRadius="sm"
                          _hover={{ bg: 'gray.100' }}
                        >
                          <Checkbox
                            isChecked={checked}
                            colorScheme="purple"
                            onChange={(e) => {
                              setSelectedStopIds((prev) => {
                                const next = new Set(prev);
                                if (e.target.checked) next.add(s.id);
                                else next.delete(s.id);
                                return next;
                              });
                            }}
                          />
                          <Text noOfLines={1} flex={1} color={checked ? 'gray.800' : 'gray.500'}>
                            {s.name}
                          </Text>
                        </HStack>
                      );
                    })}
                  </VStack>
                </Box>
              )}

              <Text fontSize="xs" color="gray.500">
                We'll optimize the stop order and show the route on the map here, with your live GPS location.
              </Text>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onRouteClose} isDisabled={routeBuilding}>Cancel</Button>
            <Button
              colorScheme="purple"
              leftIcon={<Box as={FaRoute} />}
              onClick={buildRoute}
              isLoading={routeBuilding}
              loadingText="Optimizing…"
            >
              Show route
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

function DrawRow({ draw, onOpen, onLogOutcome, onAddToRoute, isMine, isUnassigned, dataTour }) {
  const outcomeCfg = draw.outcome_code ? getOutcomeConfig(draw.outcome_code) : null;
  const phone = dialDigits(draw.patient_phone);
  return (
    <Flex
      data-tour={dataTour}
      bg="white"
      borderWidth="1px"
      borderColor="gray.200"
      borderRadius="md"
      p={3}
      direction={{ base: 'column', md: 'row' }}
      align={{ base: 'stretch', md: 'center' }}
      gap={3}
      _hover={{ borderColor: 'blue.300', boxShadow: 'sm' }}
      cursor="pointer"
      onClick={onOpen}
    >
      <Box flex={1} minW={0}>
        <HStack spacing={2} mb={1} flexWrap="wrap">
          <Text fontWeight="700" fontSize="sm" noOfLines={1}>
            <Icon as={FaUser} mr={1.5} color="gray.400" boxSize={3} />
            {draw.patient_name || 'Unnamed patient'}
          </Text>
          {draw.draw_code && <Tag size="sm" colorScheme="gray" variant="subtle">{draw.draw_code}</Tag>}
          {draw.lab_order_number && (
            <Tag size="sm" colorScheme="blue" variant="subtle">Order #{draw.lab_order_number}</Tag>
          )}
          {isUnassigned && (
            <Badge fontSize="2xs" colorScheme="purple" borderRadius="full" variant="outline">Available</Badge>
          )}
          {isMine && (
            <Badge fontSize="2xs" colorScheme="blue" borderRadius="full">Mine</Badge>
          )}
          {/* Drawer-side availability badge — clearer than the raw status enum:
              "Available" / "In Progress" / "Drawn" / "Completed" / "Not Drawable" */}
          {(() => {
            const avail = getDrawAvailability(draw);
            return (
              <Badge fontSize="2xs" colorScheme={avail.color} borderRadius="full">
                {avail.label}
              </Badge>
            );
          })()}
          {outcomeCfg && (
            <Badge fontSize="2xs" colorScheme={outcomeCfg.color} borderRadius="full" variant="solid">
              {outcomeCfg.label}
            </Badge>
          )}
          {draw.followup_required && (
            <Badge fontSize="2xs" colorScheme="red" borderRadius="full" variant="outline">
              Needs follow-up
            </Badge>
          )}
        </HStack>
        <HStack spacing={4} fontSize="xs" color="gray.600" flexWrap="wrap">
          <HStack spacing={1.5}>
            <Icon as={FaMapMarkerAlt} color="gray.400" boxSize={3} />
            <Text noOfLines={1}>{fmtAddress(draw) || 'No address'}</Text>
          </HStack>
          {fmtAddress(draw) && (
            <Button
              as="a"
              href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(fmtAddress(draw))}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              size="xs"
              h="20px"
              px={2}
              variant="outline"
              colorScheme="blue"
              leftIcon={<Icon as={FaMapMarkedAlt} boxSize={2.5} />}
            >
              Map
            </Button>
          )}
          {(draw.blood_collection_time || draw.availability_start_date) && (
            <HStack spacing={1.5}>
              <Icon as={FaCalendarAlt} color="gray.400" boxSize={3} />
              <Text>{formatSchedule(draw) || fmtDate(draw.blood_collection_time)}</Text>
            </HStack>
          )}
        </HStack>
        {draw.outcome_notes && (
          <HStack spacing={1.5} fontSize="xs" color="gray.600" mt={1.5}>
            <Icon as={FaStickyNote} color="gray.400" boxSize={3} />
            <Text noOfLines={2}>{draw.outcome_notes}</Text>
          </HStack>
        )}
        {/* Where this spun specimen goes — assigned by admin, read-only here. */}
        {draw.drop_location && (
          <HStack spacing={1.5} fontSize="xs" color="green.700" mt={1.5} align="flex-start"
            bg="green.50" borderWidth="1px" borderColor="green.200" borderRadius="md" px={2} py={1}>
            <Icon as={FaLocationArrow} color="green.500" boxSize={3} mt="2px" flexShrink={0} />
            <Text><Text as="span" fontWeight="700">Drop at:</Text> {draw.drop_location}</Text>
          </HStack>
        )}
        {Array.isArray(draw.tags) && draw.tags.length > 0 && (
          <HStack spacing={1} mt={1.5} flexWrap="wrap">
            {draw.tags.map((t) => (
              <Tag key={t} size="sm" colorScheme={tagColor(t)} borderRadius="full">{t}</Tag>
            ))}
          </HStack>
        )}
      </Box>
      {/* Actions: a stacked side column on desktop, a full-width wrapping row
          with larger tap targets on mobile. */}
      <Stack
        direction={{ base: 'row', md: 'column' }}
        spacing={2}
        align="stretch"
        w={{ base: 'full', md: 'auto' }}
        flexWrap={{ base: 'wrap', md: 'nowrap' }}
      >
        {phone && (
          <HStack spacing={2} justify="center">
            <IconButton
              as="a"
              href={`tel:${phone}`}
              size={{ base: 'md', md: 'xs' }}
              colorScheme="green"
              icon={<Box as={FaPhone} />}
              aria-label={`Call ${draw.patient_name || 'patient'}`}
              onClick={(e) => e.stopPropagation()}
            />
            <IconButton
              as="a"
              href={`sms:${phone}`}
              size={{ base: 'md', md: 'xs' }}
              colorScheme="blue"
              variant="outline"
              icon={<Box as={FaSms} />}
              aria-label={`Text ${draw.patient_name || 'patient'}`}
              onClick={(e) => e.stopPropagation()}
            />
          </HStack>
        )}
        {isUnassigned && onAddToRoute && (
          <Button
            size={{ base: 'md', md: 'xs' }}
            flex={{ base: 1, md: 'unset' }}
            colorScheme="purple"
            leftIcon={<Box as={FaRoute} />}
            onClick={(e) => { e.stopPropagation(); onAddToRoute(); }}
          >
            Add to route
          </Button>
        )}
        {/* Log outcome only shows for draws already on the phleb's route. On the
            Available list we keep it to just "Add to route" + "Open" — the phleb
            claims it first, then updates status from their route or the detail page. */}
        {!isUnassigned && (
          <Button
            size={{ base: 'md', md: 'xs' }}
            flex={{ base: 1, md: 'unset' }}
            colorScheme="teal"
            leftIcon={<Box as={FaClipboardCheck} />}
            onClick={(e) => { e.stopPropagation(); onLogOutcome(); }}
          >
            {outcomeCfg ? 'Update status' : 'Log outcome'}
          </Button>
        )}
        <Button
          size={{ base: 'md', md: 'xs' }}
          flex={{ base: 1, md: 'unset' }}
          colorScheme="blue"
          variant="outline"
          onClick={(e) => { e.stopPropagation(); onOpen(); }}
        >
          Open
        </Button>
      </Stack>
    </Flex>
  );
}

// react-icons rendered via Chakra's Icon for consistent sizing.
function Icon({ as: As, ...props }) {
  return <Box as={As} {...props} />;
}

const STATUS_PIN_COLOR = {
  pending: '#ED8936',           // orange
  open: '#ED8936',
  in_progress: '#3182CE',       // blue
  scheduled: '#3182CE',
  waiting_to_be_received: '#805AD5', // purple
  waiting_on_lab_results: '#DD6B20',
  completed: '#38A169',         // green
  cancelled: '#A0AEC0',         // gray
  needs_reschedule: '#E53E3E',  // red
  waiting_for_required_info: '#D69E2E',
};

function pinIconFor(status) {
  if (typeof window === 'undefined' || !window.google || !window.google.maps) return undefined;
  const color = STATUS_PIN_COLOR[status] || '#3182CE';
  // SVG circle marker via data URL — works without external image assets.
  return {
    path: window.google.maps.SymbolPath.CIRCLE,
    fillColor: color,
    fillOpacity: 0.95,
    strokeColor: '#fff',
    strokeWeight: 2,
    scale: 9,
  };
}

function supplyIcon() {
  if (typeof window === 'undefined' || !window.google || !window.google.maps) return undefined;
  // Yellow star with a dark outline so it stands out next to the colored patient circles.
  return {
    path: 'M 0,-12 L 3.5,-3.7 L 12,-3.7 L 5,1.5 L 7.5,9.8 L 0,4.8 L -7.5,9.8 L -5,1.5 L -12,-3.7 L -3.5,-3.7 Z',
    fillColor: '#D69E2E',
    fillOpacity: 1,
    strokeColor: '#7B5708',
    strokeWeight: 1.5,
    scale: 1,
  };
}

function DrawsMap({ isLoaded, draws, geocoded, setGeocoded, geocodeAttempted, selectedId, setSelectedId, onOpenDetail, phlebId }) {
  const [supplySelected, setSupplySelected] = useState(null);
  // Resolve coords from row → cache → null.
  //
  // Rejects nonsense values that were leaking through before:
  //   • (0, 0) — almost always a sentinel from a prior failed geocode write,
  //     not a real patient in the Atlantic. Markers were rendering hundreds
  //     of miles off the visible map and looked invisible.
  //   • NaN / non-finite numbers from bad imports or string fields like 'NaN'.
  //   • lat/lng outside earth's valid range.
  const validCoord = (lat, lng) => {
    // Explicit miss check BEFORE coercion: Number(null) and Number('') both
    // return 0, so a row with one real coord and one null would otherwise pass
    // as { lat: 0, lng: -73.x } and place a pin off the African coast.
    const isMissing = (v) => v === null || v === undefined || v === '';
    if (isMissing(lat) || isMissing(lng)) return null;
    const la = Number(lat), ln = Number(lng);
    if (!Number.isFinite(la) || !Number.isFinite(ln)) return null;
    if (la === 0 && ln === 0) return null;
    if (la < -90 || la > 90 || ln < -180 || ln > 180) return null;
    return { lat: la, lng: ln };
  };
  const coordsFor = (d) => {
    const persisted = validCoord(d.patient_lat, d.patient_lng);
    if (persisted) return persisted;
    const cached = geocoded[d.id];
    if (cached) return cached;
    return null;
  };

  // Best-effort geocode for rows with no lat/lng. Throttled and limited so we
  // don't blast through the Google Geocoding quota. Persists results back to
  // submissions.patient_lat/lng so future loads skip this step entirely.
  useEffect(() => {
    if (!isLoaded || !draws.length) return;
    if (typeof window === 'undefined' || !window.google?.maps?.Geocoder) return;

    // Geocodable if we have ANY locational hint — street+city is ideal but
    // even ZIP alone drops a pin in the right neighborhood. Previously this
    // required both street AND city, so any patient missing one (very common
    // for partial-intake rows) silently failed forever and the "Mapping N
    // more…" badge never cleared.
    const buildAddress = (d) => {
      const street = d.patient_street || d.patient_address;
      const parts = [street, d.patient_city, d.patient_state, d.patient_zip].filter(Boolean);
      return parts.join(', ');
    };
    const todo = draws.filter((d) => {
      // Skip only if the persisted coords are usable (rejecting (0,0) and
      // other bogus values that were silently invisible before).
      if (coordsFor(d)) return false;
      if (geocodeAttempted.current.has(d.id)) return false;
      // Any of: street, city, ZIP — enough to ask Google to do its best.
      return !!(d.patient_street || d.patient_address || d.patient_city || d.patient_zip);
    }).slice(0, 25); // hard cap per render to stay polite with Google's quota

    if (todo.length === 0) return;
    const geocoder = new window.google.maps.Geocoder();

    let cancelled = false;
    (async () => {
      for (const d of todo) {
        if (cancelled) break;
        geocodeAttempted.current.add(d.id);
        const addressStr = buildAddress(d);
        if (!addressStr) continue;
        try {
          const res = await new Promise((resolve) => {
            geocoder.geocode({ address: addressStr }, (results, status) => {
              if (status === 'OK' && results?.[0]) resolve(results[0].geometry.location);
              else resolve(null);
            });
          });
          if (cancelled || !res) continue;
          const lat = res.lat(), lng = res.lng();
          setGeocoded((prev) => ({ ...prev, [d.id]: { lat, lng } }));
          // Persist so we don't pay for this geocode again next load.
          supabase.from('submissions').update({ patient_lat: lat, patient_lng: lng }).eq('id', d.id).then(() => {});
          await new Promise((r) => setTimeout(r, 120));
        } catch (_) { /* ignore single-row failures */ }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, draws]);

  const withCoords = draws.map((d) => ({ d, p: coordsFor(d) })).filter((x) => x.p);
  // Split "missing coords" into "we're working on it" vs "we never can": the
  // first set is being geocoded; the second has no address at all and will
  // never get a pin, so tell the user instead of leaving the count vague.
  const stillGeocoding = draws.filter((d) => {
    if (coordsFor(d)) return false;
    return !!(d.patient_street || d.patient_address || d.patient_city || d.patient_zip);
  }).length;
  const noAddress = draws.filter((d) => {
    if (coordsFor(d)) return false;
    return !(d.patient_street || d.patient_address || d.patient_city || d.patient_zip);
  }).length;
  const sel = withCoords.find((x) => x.d.id === selectedId);

  return (
    <Box borderWidth="1px" borderRadius="md" overflow="hidden" h="560px" bg="white" position="relative">
      {isLoaded ? (
        <GoogleMap
          mapContainerStyle={{ width: '100%', height: '100%' }}
          center={NYC_CENTER}
          zoom={11}
          options={{ mapTypeControl: false, streetViewControl: false, fullscreenControl: false }}
        >
          {withCoords.map(({ d, p }) => (
            <Marker
              key={d.id}
              position={p}
              icon={pinIconFor(d.status)}
              onClick={() => { setSupplySelected(null); setSelectedId(d.id); }}
            />
          ))}
          {SERVICE_LOCATIONS.map((s) => (
            <Marker
              key={s.id}
              position={{ lat: s.lat, lng: s.lng }}
              icon={supplyIcon()}
              title={s.name}
              onClick={() => { setSelectedId(null); setSupplySelected(s); }}
            />
          ))}
          {sel && (
            <InfoWindow position={sel.p} onCloseClick={() => setSelectedId(null)}>
              <Box minW="220px" maxW="280px" p={1}>
                <Text fontSize="sm" fontWeight="700">{sel.d.patient_name}</Text>
                {sel.d.lab_order_number && (
                  <Text fontSize="xs" color="blue.700" fontWeight="700">Order #{sel.d.lab_order_number}</Text>
                )}
                <Text fontSize="xs" color="gray.600">{fmtAddress(sel.d)}</Text>
                {(sel.d.blood_collection_time || sel.d.availability_start_date) && (
                  <Text fontSize="xs" color="gray.500" mt={1}>{formatSchedule(sel.d) || fmtDate(sel.d.blood_collection_time)}</Text>
                )}
                <HStack spacing={1.5} mt={2} flexWrap="wrap">
                  {dialDigits(sel.d.patient_phone) && (
                    <>
                      <Button
                        as="a"
                        href={`tel:${dialDigits(sel.d.patient_phone)}`}
                        size="xs"
                        colorScheme="green"
                        leftIcon={<Box as={FaPhone} />}
                      >
                        Call
                      </Button>
                      <Button
                        as="a"
                        href={`sms:${dialDigits(sel.d.patient_phone)}`}
                        size="xs"
                        colorScheme="blue"
                        variant="outline"
                        leftIcon={<Box as={FaSms} />}
                      >
                        Text
                      </Button>
                    </>
                  )}
                  {fmtAddress(sel.d) && (
                    <Button
                      as="a"
                      href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(fmtAddress(sel.d))}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      size="xs"
                      colorScheme="blue"
                      variant="outline"
                      leftIcon={<Box as={FaMapMarkedAlt} />}
                    >
                      Directions
                    </Button>
                  )}
                  <Button
                    size="xs"
                    colorScheme="blue"
                    onClick={() => onOpenDetail(sel.d.id)}
                  >
                    Open
                  </Button>
                </HStack>
              </Box>
            </InfoWindow>
          )}
          {supplySelected && (
            <InfoWindow
              position={{ lat: supplySelected.lat, lng: supplySelected.lng }}
              onCloseClick={() => setSupplySelected(null)}
            >
              <Box minW="220px" maxW="280px" p={1}>
                <HStack mb={1} spacing={2}>
                  <Box w="10px" h="10px" bg="#D69E2E" borderRadius="sm" />
                  <Text fontSize="2xs" fontWeight="700" color="gray.500" textTransform="uppercase" letterSpacing="0.05em">
                    Supply Pickup
                  </Text>
                </HStack>
                <Text fontSize="sm" fontWeight="700">{supplySelected.name}</Text>
                <Text fontSize="xs" color="gray.600">{supplySelected.address}</Text>
                <Button
                  as="a"
                  mt={2}
                  size="xs"
                  colorScheme="blue"
                  href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(supplySelected.address)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Directions
                </Button>
              </Box>
            </InfoWindow>
          )}
        </GoogleMap>
      ) : (
        <Flex h="100%" align="center" justify="center"><Spinner /></Flex>
      )}

      {/* Legend */}
      <Box
        position="absolute"
        bottom={3}
        left={3}
        bg="whiteAlpha.900"
        borderWidth="1px"
        borderRadius="md"
        px={3}
        py={2}
        boxShadow="sm"
      >
        <Text fontSize="2xs" fontWeight="700" color="gray.500" textTransform="uppercase" letterSpacing="0.05em" mb={1.5}>
          Status
        </Text>
        <SimpleGrid columns={2} spacingX={4} spacingY={1}>
          {[
            ['Open', '#ED8936'],
            ['In Progress', '#3182CE'],
            ['Completed', '#38A169'],
            ['Cancelled', '#A0AEC0'],
            ['Needs Reschedule', '#E53E3E'],
            ['Waiting Info', '#D69E2E'],
          ].map(([label, color]) => (
            <HStack key={label} spacing={1.5}>
              <Box w="10px" h="10px" borderRadius="full" bg={color} borderWidth="2px" borderColor="white" boxShadow="0 0 0 1px rgba(0,0,0,0.1)" />
              <Text fontSize="2xs" color="gray.700">{label}</Text>
            </HStack>
          ))}
          <HStack spacing={1.5} gridColumn="1 / -1">
            <Text as="span" color="#D69E2E" fontSize="md" lineHeight="1">★</Text>
            <Text fontSize="2xs" color="gray.700">Supply Pickup</Text>
          </HStack>
        </SimpleGrid>
      </Box>

      {(stillGeocoding > 0 || noAddress > 0) && (
        <VStack
          spacing={0.5} align="end" position="absolute" top={3} right={3}
          bg="whiteAlpha.900" px={2} py={1} borderRadius="md"
        >
          {stillGeocoding > 0 && (
            <Text fontSize="xs" color="gray.500">Mapping {stillGeocoding} more…</Text>
          )}
          {noAddress > 0 && (
            <Text fontSize="xs" color="orange.500" fontWeight="600">
              {noAddress} without address
            </Text>
          )}
        </VStack>
      )}
    </Box>
  );
}
